import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { spokenWhisper } from '../../lib/speedToLead/voiceNcco';
import {
  DEFAULT_MISSED_TEMPLATE,
  DEFAULT_OUTBOUND_TEMPLATE,
  renderOutboundWhisper,
  DEFAULT_WHISPER_TEMPLATE,
  MESSAGE_MAX_CHARS,
  PREVIEW_VALUES,
  renderMissed,
  renderWhisper,
  validateMessageTemplate,
  type MessageKind,
} from '../../lib/speedToLead/spokenMessages';

// The two spoken messages of inbound call forwarding, editable by Kyck (Settings -> Speed to Lead) and by the
// club (the portal's "Who we call"). ONE component so the wording, the live preview and the validation are the
// same on both surfaces. ⚠️ The preview shows the fixed key prompt on the end of the whisper: the club can see
// it is always there and that it is not theirs to edit.

function Field({
  kind, label, hint, value, onChange, readOnly, preview,
}: {
  kind: MessageKind; label: string; hint: string; value: string; onChange: (v: string) => void; readOnly: boolean; preview: string;
}) {
  const check = validateMessageTemplate(kind, value);
  const over = value.replace(/\s+/g, ' ').trim().length;
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-foreground" htmlFor={`msg-${kind}`}>{label}</label>
      <Input id={`msg-${kind}`} value={value} disabled={readOnly} maxLength={600} placeholder={kind === 'whisper' ? DEFAULT_WHISPER_TEMPLATE : DEFAULT_MISSED_TEMPLATE} onChange={(e) => onChange(e.target.value)} aria-invalid={!check.ok} />
      <p className="text-xs text-muted-foreground">{hint} {over}/{MESSAGE_MAX_CHARS}. Leave empty to use the default.</p>
      {!check.ok && <p role="alert" className="text-xs text-destructive">{check.error}</p>}
      <p className="rounded bg-muted px-2 py-1 text-xs text-foreground" data-testid={`preview-${kind}`}><span className="text-muted-foreground">Sounds like: </span>{preview}</p>
    </div>
  );
}

/**
 * The OUTBOUND staff whisper (Stage 3C): what the team hears when Speed to Lead rings them about a new lead, before
 * the fixed key prompt. Kyck-only (the club never edits it). The preview shows the key prompt on the end, always.
 */
export function OutboundWhisperEditor({ value, onChange, readOnly = false }: { value: string; onChange: (v: string) => void; readOnly?: boolean }) {
  const check = validateMessageTemplate('outbound', value);
  const preview = spokenWhisper(renderOutboundWhisper(check.ok ? check.value : null, { leadName: PREVIEW_VALUES.leadName, club: PREVIEW_VALUES.club, source: PREVIEW_VALUES.source }));
  const len = value.replace(/\s+/g, ' ').trim().length;
  return (
    <div className="space-y-1" data-testid="outbound-whisper-editor">
      <label className="block text-sm font-medium text-foreground" htmlFor="msg-outbound">What your team hears when Speed to Lead rings them</label>
      <Input id="msg-outbound" value={value} disabled={readOnly} maxLength={600} placeholder={DEFAULT_OUTBOUND_TEMPLATE} onChange={(e) => onChange(e.target.value)} aria-invalid={!check.ok} />
      <p className="text-xs text-muted-foreground">Use {'{lead_name}'}, {'{club}'} and {'{source}'}. "Press any key to take the call." is always added at the end. {len}/{MESSAGE_MAX_CHARS}. Leave empty to use the default.</p>
      {!check.ok && <p role="alert" className="text-xs text-destructive">{check.error}</p>}
      <p className="rounded bg-muted px-2 py-1 text-xs text-foreground" data-testid="preview-outbound"><span className="text-muted-foreground">Sounds like: </span>{preview}</p>
    </div>
  );
}

export interface MessagesEditorProps {
  whisperMessage: string;
  missedCallMessage: string;
  onChange: (next: { whisperMessage: string; missedCallMessage: string }) => void;
  /** Read-only (Kyck has locked the messages, or the viewer cannot edit). */
  readOnly?: boolean;
}

export function messagesValid(whisperMessage: string, missedCallMessage: string): boolean {
  return validateMessageTemplate('whisper', whisperMessage).ok && validateMessageTemplate('missed', missedCallMessage).ok;
}

export function MessagesEditor({ whisperMessage, missedCallMessage, onChange, readOnly = false }: MessagesEditorProps) {
  const w = validateMessageTemplate('whisper', whisperMessage);
  const m = validateMessageTemplate('missed', missedCallMessage);
  const whisperPreview = spokenWhisper(renderWhisper(w.ok ? w.value : null, PREVIEW_VALUES));
  const missedPreview = renderMissed(m.ok ? m.value : null, PREVIEW_VALUES.club);
  return (
    <div className="space-y-4" data-testid="messages-editor">
      <Field kind="whisper" label="What your team hears when they pick up" hint={`Use {lead_name}, {club}, {source} and {pool}. "Press any key to take the call." is always added at the end.`} value={whisperMessage} readOnly={readOnly} preview={whisperPreview} onChange={(v) => onChange({ whisperMessage: v, missedCallMessage })} />
      <Field kind="missed" label="What the caller hears when nobody can take the call" hint="Use {club}." value={missedCallMessage} readOnly={readOnly} preview={missedPreview} onChange={(v) => onChange({ whisperMessage, missedCallMessage: v })} />
      {!readOnly && (
        <Button type="button" variant="outline" size="sm" onClick={() => onChange({ whisperMessage: '', missedCallMessage: '' })}>Reset to default</Button>
      )}
    </div>
  );
}
